// The "Request New Forecast" Inputs page for the forecasting self-serve flow.
// QA-aware: the channel/platform menu comes from a Data QA run's viable combos
// (the shared run selection, fetched once at ForecastingPage) — latest by
// default, or whichever the RunSelector picks; non-viable platforms show greyed
// with the KPI they fail.
// Built from the Figma "Request New Forecast" modal spec (node 6036-55033).
// TODO(forecasting-self-serve): submit is front-end only until the
// startNewForecast/saveDraftForecast lambdas land (they are TODO stubs whose
// arg shapes match this form). The Figma's "Statistical Model" dropdown is
// omitted until prior-model reuse actually exists server-side.
import './RequestForecast.scss';
import { useEffect, useMemo, useState } from 'react';
import { AlertDialog } from '@Components/AlertDialog';
import { Button } from '@Components/Button/Button';
import { Appearance } from '@Components/designConstants';
import { FullPageSpinner } from '@Components/Spinner';
import { useSetError } from '@src/redux/modals';
import { useCompanyInfo } from '../../../utils/hooks/useCompanyInfo';
import { GoToTab } from '../../../utils/hooks/useNav';
import { RunSelectionBanner } from '../components/RunSelectionBanner';
import { DataQaConfig, PageTab, PageTabValue } from '../forecasting-constants';
import { scopeQaGroups } from '../forecasting-utils';
import { DataQaRun, ForecastDataQaResult } from '../useForecastDataQa';
import { RequestForm } from './components/RequestForm';

interface RequestForecastProps {
  savedScope?: DataQaConfig;
  goToTab: GoToTab<PageTabValue>;
  /** Shared Data QA runs, fetched once at ForecastingPage. */
  qaState: ForecastDataQaResult;
  /** Resolved shared run selection (newest by default); null until runs load. */
  selectedRun: DataQaRun | null;
  onSelectRun: (s3Key: string) => void;
  /** Set when onboarding kicks off a run; shows the "run in progress" modal. Its
   *  `id` is null when the lambda returned no parseable id (notice still shows). */
  inProgressRun?: { id: string | null } | null;
  /** Clears inProgressRun in the parent on dismiss. */
  onDismissInProgress?: () => void;
}

const RequestForecast = ({
  savedScope,
  goToTab,
  qaState,
  selectedRun,
  onSelectRun,
  inProgressRun,
  onDismissInProgress = () => {}
}: RequestForecastProps) => {
  const setError = useSetError();
  const { cid: company } = useCompanyInfo();
  const { refetch, isRefreshing } = qaState;
  const [formNonce, setFormNonce] = useState(0);

  const runs = qaState.status === 'ready' ? qaState.runs : null;

  // The banner's combination count is scope-narrowed to match the form's menu
  // (and the Data QA page's count), not the full run — see scopeQaGroups.
  const combinationsCount = useMemo(
    () => (selectedRun ? scopeQaGroups(selectedRun.data.groups, savedScope).groups.length : 0),
    [selectedRun, savedScope]
  );

  // The QA run and the saved scope load independently, so the scope can resolve
  // after the form mounted off the full QA universe. Folding it into the remount
  // key re-seeds the card against the narrowed menu when it arrives.
  const scopeKey = `${savedScope?.channels?.join(',') ?? ''}|${savedScope?.kpis?.join(',') ?? ''}`;

  const errorMessage = qaState.status === 'error' ? qaState.message : null;
  useEffect(() => {
    if (errorMessage) {
      setError({ message: `Couldn't load the Data QA results: ${errorMessage}` });
    }
  }, [errorMessage, setError]);

  return (
    <div className='req-fc' data-testid='request-forecast'>
      {/* Shown when arriving from onboarding; dismissing clears it so it doesn't reappear. */}
      <AlertDialog
        show={Boolean(inProgressRun)}
        onHide={onDismissInProgress}
        title='Data QA run in progress'
        primaryAction={{ label: 'Got it' }}
        body={
          <>
            <p>
              Data QA is running for your selected channels and KPIs. We&rsquo;ll email you when
              it&rsquo;s ready. Refresh this page once you get the email to set up your forecast.
            </p>
            {inProgressRun?.id && (
              <p>
                Run ID: <code>{inProgressRun.id}</code>
              </p>
            )}
          </>
        }
      />

      {/* useCompanyInfo returns cid '' until hydrated — hold the loading
            state so the form never mounts against an empty company. */}
      {(qaState.status === 'loading' || !company) && <FullPageSpinner />}
      {qaState.status === 'empty' && (
        <div className='req-state' data-testid='request-empty'>
          <p>
            No Data QA run found for this client yet — viability has to be established before a
            forecast can be requested.
          </p>
          <Button
            appearance={Appearance.OUTLINED}
            onClick={() => goToTab(PageTab.FORECAST_ONBOARDING)}
          >
            Set up forecasting
          </Button>
        </div>
      )}
      {runs && selectedRun && Boolean(company) && (
        <>
          {/* Picker stays mounted outside the keyed form so switching runs doesn't drop focus. */}
          <RunSelectionBanner
            label='Data QA run'
            testId='request-run-selection-banner'
            runs={runs}
            selectedS3Key={selectedRun.s3Key}
            dateRange={selectedRun.data.date_range}
            combinationsCount={combinationsCount}
            onSelectRun={onSelectRun}
            onRefresh={refetch}
            isRefreshing={isRefreshing}
          />
          {/* key remounts the form on run change, scope resolve, or discard —
              stale card/KPI state can't survive a changed menu universe */}
          <RequestForm
            key={`${selectedRun.s3Key}:${scopeKey}:${formNonce}`}
            qa={selectedRun.data}
            qaS3Key={selectedRun.s3Key}
            company={company}
            savedScope={savedScope}
            onDiscard={() => setFormNonce(n => n + 1)}
            onGoToScalingResults={() => goToTab(PageTab.SCALING_RESULTS)}
          />
        </>
      )}
    </div>
  );
};

export { RequestForecast };
